/**
 * Routine Card Component
 * Displays a routine preview in chat with "Add to Hevy" integration
 */

import { Plus } from 'lucide-react'
import { memo, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useUserCredentials } from '../../api/hooks/useCredentials'
import { useExerciseTemplates } from '../../api/hooks/useExerciseTemplates'
import { cn } from '../../lib/utils'
import { createHevyRoutine } from '../../services/hevyPushService'
import { UnifiedButton } from '../ui/unified-button'

interface RoutineCardProps {
  routineConfig: {
    id: string
    name: string
    exercises: Array<{
      exercise_template_id: string
      sets: Array<{
        type: 'normal' | 'warmup' | 'failure' | 'dropset'
        reps?: number | null
        weight_kg?: number | null
        duration_seconds?: number | null
        distance_meters?: number | null
      }>
      superset_id?: string
      rest_seconds?: number
      notes?: string
    }>
    folder_id?: number
    notes?: string
    totalSets: number
    estimatedMinutes: number
    created_at: string
  }
  _summary: string
  className?: string
}

const RoutineCardComponent = ({ routineConfig, _summary, className }: RoutineCardProps) => {
  const { name, exercises, totalSets, estimatedMinutes, notes } = routineConfig
  const { exercises: exerciseTemplates } = useExerciseTemplates()
  const { data: userCredentials } = useUserCredentials()
  const [isAdding, setIsAdding] = useState(false)
  const [editableName, setEditableName] = useState(name)
  const [isEditingName, setIsEditingName] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus input when starting to edit
  useEffect(() => {
    if (isEditingName && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditingName])

  // Handle name editing
  const handleNameClick = () => {
    setIsEditingName(true)
  }

  const handleNameSubmit = () => {
    setIsEditingName(false)
    // Ensure we have a valid name
    if (!editableName.trim()) {
      setEditableName(name) // Revert to original name if empty
    }
  }

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleNameSubmit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setEditableName(name) // Revert to original name
      setIsEditingName(false)
    }
  }

  const handleAddToHevy = async () => {
    if (!userCredentials?.apiKey || isAdding) return

    setIsAdding(true)
    try {
      // Convert exercises to Hevy format
      const hevyExercises = exercises.map((exercise) => ({
        exercise_template_id: exercise.exercise_template_id,
        superset_id: exercise.superset_id || null,
        rest_seconds: exercise.rest_seconds || 90,
        notes: exercise.notes || '',
        sets: exercise.sets.map((set) => {
          // Always use null for weight_kg - users will fill in their own weights
          let reps = set.reps || null

          // If no reps are specified and it's not a time/distance exercise, default to bodyweight reps
          if (!reps && !set.duration_seconds && !set.distance_meters) {
            reps = 10 // Default bodyweight reps
          }

          return {
            type: set.type,
            weight_kg: null, // Always null - users fill in their own weights
            reps,
            distance_meters: set.distance_meters || null,
            duration_seconds: set.duration_seconds || null,
            custom_metric: null
          }
        })
      }))


      // Validate name before API call
      if (!editableName || editableName.trim().length === 0) {
        throw new Error('Routine name is required but was empty or undefined')
      }

      await createHevyRoutine(
        userCredentials.apiKey,
        editableName.trim(),
        hevyExercises,
        routineConfig.folder_id,
        notes || ''
      )

      toast.success(`"${editableName}" added to Hevy successfully!`)
    } catch (error) {
      console.error('Failed to add routine to Hevy:', error)
      toast.error('Failed to add routine to Hevy. Please check your API key.')
    } finally {
      setIsAdding(false)
    }
  }

  const hasApiKey = !!(
    userCredentials?.apiKey &&
    userCredentials.apiKey !== 'CSV_IMPORT' &&
    userCredentials.apiKey.length > 10
  )

  return (
    <div className={cn(className)}>
      <div className="bg-muted relative rounded-lg border p-4 transition-colors">
        {/* Header */}
        <div className="mb-2 flex items-center justify-between">
          <div className="flex flex-col">
            {isEditingName ? (
              <input
                ref={inputRef}
                type="text"
                value={editableName}
                onChange={(e) => setEditableName(e.target.value)}
                onBlur={handleNameSubmit}
                onKeyDown={handleNameKeyDown}
                className="focus:bg-background/20 focus:border-border -mx-1 truncate border-none bg-transparent px-1 text-base font-semibold outline-none focus:rounded focus:border"
                placeholder="Enter routine name..."
              />
            ) : (
              <span
                className="hover:bg-muted-foreground/10 -mx-1 cursor-pointer truncate rounded px-1 text-base font-semibold transition-colors"
                onClick={handleNameClick}
                title="Click to edit routine name"
              >
                {editableName}
              </span>
            )}
            <div className="text-muted-foreground mt-1 flex items-center gap-3 text-xs">
              <span>{exercises.length} exercises</span>
              <span>{totalSets} sets</span>
              <span>{estimatedMinutes}m</span>
            </div>
          </div>

          <UnifiedButton
            icon={Plus}
            onClick={hasApiKey ? handleAddToHevy : undefined}
            disabled={!hasApiKey || isAdding}
            loading={isAdding}
            size="sm"
            className="mb-auto px-2 text-xs"
            title={!hasApiKey ? 'Configure Hevy API key in Settings' : undefined}
          >
            {isAdding ? 'Adding...' : hasApiKey ? 'Add to Hevy' : 'No API Key'}
          </UnifiedButton>
        </div>

        {/* Exercise names */}
        <p className="text-muted-foreground mt-2 text-xs">
          {exercises
            .map((exercise, index) => {
              const template = exerciseTemplates?.find(
                (t) => t.id === exercise.exercise_template_id
              )
              return template?.name || `Exercise ${index + 1}`
            })
            .join(', ')}
        </p>
      </div>
    </div>
  )
}

export const RoutineCard = memo(RoutineCardComponent)
